/**
 * CORE-BN — Front-of-House Attention Capacity · observation aggregation
 *
 * Constitutional authority: CORE-BN Inputs table, and CORE-BN-PRIV-001.
 *
 * The privacy boundary lives here
 * -------------------------------
 * This is the ONLY layer that ever touches individual-scoped rows. Its
 * whole job is to collapse them into shop-scoped counts before anything
 * downstream sees them.
 *
 * CORE-BN-PRIV-001, recorded at design stage rather than after build:
 *
 *   "Every candidate input to this model is individual-scoped personal
 *    operational data. A shop-level attention figure derived from a small
 *    number of named people is not anonymous... the easiest possible
 *    implementation is also the one that would quietly convert a capacity
 *    model into individual surveillance."
 *
 * The mitigation is that the functions below never SELECT a person
 * column, never accept one on their input shapes, and never emit one.
 * They cannot leak an identifier they were never given. Where a person
 * identifier is genuinely required to answer a shop-level question — "how
 * many of the people clocked in right now hold a front-of-house role?" —
 * it is used transiently inside a single function and never leaves it;
 * see `countClockedInFohStaff`.
 *
 * `advisor_daily_metrics`, `advisor_action_events` and
 * `advisor_intelligence_stats` are deliberately NOT read. Each is
 * per-advisor by grain, each is the substrate the audit warned about, and
 * every question CORE-BN needs is answerable from a shop-grained source
 * instead. `advisor_decision_events` IS read, but only as a shop-scoped
 * COUNT over a time range — its `advisor_user_id` is never selected.
 *
 * Purity: no I/O, no clock. Callers pass rows and an explicit window.
 */

import { getCustodyDwellAgingLevel } from '@/lib/tickets/lifecycle/custodyAging';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
import type {
  FohAttentionObservations,
  FohClaimBasis,
  FohClaimObservation,
  FohDwellBucket,
  FohDwellDistribution,
  FohPresenceSplit,
  FohStaffingBasis,
  FohStaffingObservation,
  FohTimeClockStatus,
} from './types';

// =============================================================================
// Raw row shapes
// =============================================================================

/**
 * A front-of-house custody row, reduced to the two fields the model
 * needs. `holder_user_id` and `entered_by` exist on `ticket_custody` and
 * are deliberately absent here.
 */
export interface FohCustodyRowLite {
  custody_status: string;
  entered_at: string;
}

/**
 * A ticket, reduced to the fields that establish arrival and lobby
 * presence. No customer name, no phone, no advisor.
 *
 * Physical presence is carried as the four LOCATION fields rather than
 * as the `vehicle_present` column. That column is a transitional DB
 * mirror and is explicitly **no longer authoritative** — see
 * `src/utils/deriveVehiclePresence.ts`, whose own doctrine header says
 * "All app logic must use this function." CORE-BN consumes that
 * derivation rather than the mirror, so a shop whose trigger has not
 * fired cannot make a vehicle disappear from the attention model.
 */
export interface FohTicketRowLite {
  type: string | null;
  status: string | null;
  start_time: string | null;
  picked_up_at?: string | null;
  /** Location fields feeding `isVehiclePresent`. */
  parking_spot_id?: string | null;
  bay_id?: string | null;
  timer_status?: string | null;
  parking_lot?: boolean | null;
}

/**
 * How long after a scheduled time the shop keeps inferring that a
 * customer is present when it holds no physical evidence at all.
 *
 * Beyond this, a vehicle with no parking spot, no bay, no timer and no
 * lot flag is far more likely to be a no-show than a customer sitting
 * unnoticed in the lobby. Continuing to charge attention for it would
 * manufacture demand out of an appointment nobody kept — an estimate
 * drifting away from the shop rather than tracking it. (The real work a
 * no-show creates is a phone call, which the communication claim
 * already counts.)
 */
export const FOH_INFERRED_PRESENCE_GRACE_MS = 30 * 60_000;

/**
 * Ticket statuses that mean the vehicle is no longer claiming
 * front-of-house attention.
 *
 * The canonical enum is capitalized (`tickets_status_check`); the
 * lowercase spellings that appear elsewhere in the codebase never match
 * a production row. Both are listed so a lowercase row from a fixture or
 * an importer is also excluded rather than silently counted as live
 * demand.
 */
export const FOH_INACTIVE_TICKET_STATUSES: ReadonlyArray<string> = Object.freeze([
  'Completed',
  'Deleted',
  'Cancelled',
  'completed',
  'deleted',
  'cancelled',
]);

export function isTicketFohActive(row: FohTicketRowLite): boolean {
  if (row.picked_up_at) return false;
  const status = (row.status ?? '').trim();
  if (status && FOH_INACTIVE_TICKET_STATUSES.includes(status)) return false;
  return true;
}

// =============================================================================
// Custody claims
// =============================================================================

/**
 * The three FOH custody statuses, mapped to their claim kinds.
 *
 * Mirrors `FOH_QUEUE_STATUSES` in the custody queue. Kept as its own map
 * rather than imported so this module has no dependency on a React
 * component; `custodyAging`, which IS imported, is a pure helper and is
 * the single authority on the dwell thresholds themselves — CORE-BN
 * deliberately does not restate them.
 */
export const FOH_CUSTODY_STATUS_TO_CLAIM = Object.freeze({
  pending_advisor_accept: 'custody_pending_accept',
  with_advisor: 'custody_with_advisor',
  pending_tech_accept: 'custody_pending_tech_accept',
} as const);

export type FohCustodyStatusKey = keyof typeof FOH_CUSTODY_STATUS_TO_CLAIM;

const EMPTY_DWELL: FohDwellDistribution = Object.freeze({
  normal: 0,
  warning: 0,
  urgent: 0,
});

/**
 * Bucket live front-of-house custody rows by claim kind and dwell level.
 *
 * Dwell grading is delegated wholesale to `getCustodyDwellAgingLevel`,
 * which already owns the shop's 5/10-minute and 20/40-minute thresholds.
 * Re-deriving them here would create a second authority over the same
 * number and guarantee eventual drift — CORE-BN-GAP-003 is that the
 * existing aging result is thrown away, not that it is wrong.
 *
 * Rows in unknown custody states are ignored rather than bucketed, so a
 * future custody status cannot silently inflate front-of-house demand.
 */
export function aggregateCustodyClaims(
  rows: ReadonlyArray<FohCustodyRowLite>,
  referenceTimeIso: string,
): FohClaimObservation[] {
  const acc = new Map<
    (typeof FOH_CUSTODY_STATUS_TO_CLAIM)[FohCustodyStatusKey],
    { count: number; dwell: Record<FohDwellBucket, number> }
  >();
  for (const key of Object.keys(FOH_CUSTODY_STATUS_TO_CLAIM) as FohCustodyStatusKey[]) {
    acc.set(FOH_CUSTODY_STATUS_TO_CLAIM[key], {
      count: 0,
      dwell: { ...EMPTY_DWELL },
    });
  }

  for (const row of rows ?? []) {
    if (!row) continue;
    const claimKind =
      FOH_CUSTODY_STATUS_TO_CLAIM[row.custody_status as FohCustodyStatusKey];
    if (!claimKind) continue;
    const entry = acc.get(claimKind);
    if (!entry) continue;
    const level = getCustodyDwellAgingLevel(
      row.custody_status,
      row.entered_at,
      referenceTimeIso,
    );
    entry.count += 1;
    entry.dwell[level] += 1;
  }

  return [...acc.entries()].map(([kind, entry]) => ({
    kind,
    count: entry.count,
    basis: 'live' as FohClaimBasis,
    dwell: Object.freeze({ ...entry.dwell }),
  }));
}

// =============================================================================
// Arrival and lobby-presence claims
// =============================================================================

function inWindow(iso: string | null | undefined, startMs: number, endMs: number): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t >= startMs && t < endMs;
}

/**
 * Count vehicles **still expected to arrive** inside the window.
 *
 * An arrival is a discrete intake claim: greet, verify, capture the
 * concern, set the expectation. The shop holds no arrival EVENT, so this
 * is derived from the appointment time and is reported `'inferred'`, never
 * `'live'`.
 *
 * A vehicle that is already physically at the shop is deliberately NOT
 * counted here, even when its appointment falls inside the window. It
 * has arrived; the greet either already happened or the ticket is now
 * sitting in front-of-house custody, where `custody_pending_accept`
 * charges for it. Counting it in both places would bill the same
 * customer's intake twice and drift the estimate upward exactly when the
 * shop is busiest — the moment the model most needs to be trusted.
 */
export function countArrivalsInWindow(
  rows: ReadonlyArray<FohTicketRowLite>,
  windowStartIso: string,
  windowEndIso: string,
): number {
  const startMs = Date.parse(windowStartIso);
  const endMs = Date.parse(windowEndIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 0;
  let n = 0;
  for (const row of rows ?? []) {
    if (!row || !isTicketFohActive(row)) continue;
    if (isVehiclePresent(row)) continue; // already here — not an arrival
    if (inWindow(row.start_time, startMs, endMs)) n += 1;
  }
  return n;
}

/**
 * Count customers waiting in the lobby, split by how the shop knows.
 *
 * Unlike an arrival this is a CONTINUOUS claim — the constitution is
 * explicit that "present customers claim attention continuously" — so
 * the model charges it as a fraction of the window rather than as an
 * event.
 *
 * The split is the honest part. The shop can *observe* that a waiter's
 * vehicle is here (it holds a parking spot, a bay, a running timer, or
 * the lot flag — the canonical `isVehiclePresent` derivation). It can
 * only *infer* that a customer whose appointment has come round but
 * whose vehicle has left no physical trace is sitting in the lobby.
 * Both consume attention, so both count; but conflating them would
 * present a guess as an observation.
 *
 * Inference is bounded by `FOH_INFERRED_PRESENCE_GRACE_MS`. A waiter due
 * three hours ago with no physical trace is a no-show, not a customer
 * the shop has failed to notice.
 *
 * A waiter still expected to arrive is counted here AND as an arrival —
 * being greeted and then being waited on are two different demands, and
 * the desk feels both.
 */
export function countWaitersPresent(
  rows: ReadonlyArray<FohTicketRowLite>,
  windowStartIso: string,
  windowEndIso: string,
): FohPresenceSplit {
  const startMs = Date.parse(windowStartIso);
  const endMs = Date.parse(windowEndIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return { observed: 0, inferred: 0 };
  }
  const inferredFloorMs = startMs - FOH_INFERRED_PRESENCE_GRACE_MS;

  let observed = 0;
  let inferred = 0;
  for (const row of rows ?? []) {
    if (!row || !isTicketFohActive(row)) continue;
    if (String(row.type ?? '').toLowerCase() !== 'waiter') continue;

    if (isVehiclePresent(row)) {
      observed += 1;
      continue;
    }

    // No physical trace. Fall back to the appointment, but only while it
    // is still plausible that the customer is here.
    const startTimeMs = row.start_time ? Date.parse(row.start_time) : NaN;
    if (
      Number.isFinite(startTimeMs) &&
      startTimeMs < endMs &&
      startTimeMs >= inferredFloorMs
    ) {
      inferred += 1;
    }
  }
  return { observed, inferred };
}

/**
 * The basis a presence-derived claim should report.
 *
 * `'live'` only when every counted item is physically observed. One
 * inferred item makes the whole claim inferred, because a reader cannot
 * act on a number that is three-quarters observed as though it were
 * observed. Conservative in the direction of admitting uncertainty.
 */
export function presenceBasis(split: FohPresenceSplit): FohClaimBasis {
  if (split.inferred > 0) return 'inferred';
  return 'live';
}

// =============================================================================
// Staffing
// =============================================================================

/**
 * Front-of-house roles that supply attention.
 *
 * This mirrors `FOH_CAPABLE_ROLES` in
 * `src/hooks/tickets/useFohCapableMembers.ts` — the custody layer's
 * authority on who may hold front-of-house custody. It is restated rather
 * than imported because that module is a React Query hook that pulls in
 * Supabase and the feature-flag store, and this module must stay pure.
 * A parity test pins the two lists together so the restatement cannot
 * drift.
 */
export const FOH_ATTENTION_STAFF_ROLES: ReadonlyArray<string> = Object.freeze([
  'OWNER',
  'MANAGER',
  'ORG_ADMIN',
  'SERVICE_WRITER',
]);

export interface FohMembershipRowLite {
  /**
   * Present because the caller must be able to intersect roster with the
   * time clock. It is consumed inside this module and never emitted.
   */
  user_id: string;
  role: string;
}

export interface FohClockEntryRowLite {
  user_id: string | null;
  clock_in_time: string;
  clock_out_time: string | null;
}

/** Roster headcount: active front-of-house-capable memberships. */
export function countRosterFohStaff(
  rows: ReadonlyArray<FohMembershipRowLite>,
): number {
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    if (!row?.user_id) continue;
    if (!FOH_ATTENTION_STAFF_ROLES.includes(row.role)) continue;
    seen.add(row.user_id);
  }
  return seen.size;
}

/**
 * Clocked-in headcount: people on the clock right now who hold a
 * front-of-house role.
 *
 * This is the one place CORE-BN handles person identifiers, and it does
 * so to answer a strictly shop-level question. The identifiers are used
 * to build a set membership test and are discarded when the function
 * returns an integer. Nothing about who was counted survives the call —
 * not in the return value, not in the result object, and not on any
 * surface.
 */
export function countClockedInFohStaff(
  memberships: ReadonlyArray<FohMembershipRowLite>,
  clockEntries: ReadonlyArray<FohClockEntryRowLite>,
  referenceTimeIso: string,
): number {
  const refMs = Date.parse(referenceTimeIso);
  if (!Number.isFinite(refMs)) return 0;

  const fohUserIds = new Set<string>();
  for (const row of memberships ?? []) {
    if (!row?.user_id) continue;
    if (!FOH_ATTENTION_STAFF_ROLES.includes(row.role)) continue;
    fohUserIds.add(row.user_id);
  }
  if (fohUserIds.size === 0) return 0;

  const onTheClock = new Set<string>();
  for (const entry of clockEntries ?? []) {
    if (!entry?.user_id) continue;
    if (!fohUserIds.has(entry.user_id)) continue;
    const inMs = Date.parse(entry.clock_in_time);
    if (!Number.isFinite(inMs) || inMs > refMs) continue;
    if (entry.clock_out_time) {
      const outMs = Date.parse(entry.clock_out_time);
      if (Number.isFinite(outMs) && outMs <= refMs) continue;
    }
    onTheClock.add(entry.user_id);
  }
  return onTheClock.size;
}

/**
 * Choose the staffing basis, honouring what the time clock actually
 * said.
 *
 * The rule that matters
 * ---------------------
 * **A successfully-read time clock is authoritative, including when its
 * answer is zero.**
 *
 * An earlier revision of this function fell back to the roster whenever
 * the clocked-in count was zero, on the theory that somebody had
 * probably forgotten to punch in. That was wrong, and it was wrong in
 * the most dangerous available direction. A shop whose front desk is
 * genuinely unstaffed at 7:30 — everyone in the back, or the opener late
 * — would have been credited with its entire roster and reported
 * `comfortable` while nobody was at the counter at all. That is
 * precisely the fabricated figure Responsibility 8 forbids: "when the
 * model has no basis, the shop plans as it does now rather than on a
 * fabricated figure." Worse, this is not even a case of *no* basis. It
 * is a case of clear basis pointing at zero, overruled by a weaker
 * source because the answer was inconvenient.
 *
 * Zero clocked in is EVIDENCE, not absence of evidence. It resolves to a
 * headcount of zero on a `'clocked_in'` basis, which the model turns
 * into `'unknown'` with the reason `no_foh_staff_on_duty` — a distinct,
 * actionable statement ("nobody is clocked in to a front-of-house role")
 * rather than the vaguer "we could not establish staffing".
 *
 * The roster is used only where it is genuinely the best available
 * statement:
 *
 *   - `'disabled'`    — the shop does not use the time clock at all, so
 *                       the roster is the only statement of who works
 *                       here. Legitimate; caps confidence below `'high'`.
 *   - `'unavailable'` — the clock (or the role roster needed to filter
 *                       it) could not be read. We do not know what it
 *                       would have said, so we fall back and record the
 *                       failure, which caps confidence at `'low'`.
 *
 * A forgotten punch now surfaces as `'unknown'` rather than as invented
 * capacity. That is the correct trade: the shop is told the model cannot
 * speak, and plans as it does today.
 */
export function resolveStaffingObservation(args: {
  timeClockStatus: FohTimeClockStatus;
  clockedInCount: number;
  rosterCount: number;
}): FohStaffingObservation {
  const { timeClockStatus } = args;

  if (timeClockStatus === 'available') {
    // Authoritative, zero included. No roster fallback from here.
    return {
      headcount: Math.max(0, Math.floor(args.clockedInCount || 0)),
      basis: 'clocked_in',
      timeClockStatus,
    };
  }

  if (args.rosterCount > 0) {
    return {
      headcount: args.rosterCount,
      basis: 'roster',
      timeClockStatus,
    };
  }

  return {
    headcount: 0,
    basis: 'none' as FohStaffingBasis,
    timeClockStatus,
  };
}

/**
 * Decide what the time clock can tell us for this window.
 *
 * `'available'` requires ALL of:
 *   - the shop profile was read, so we know whether the clock is in use;
 *   - the clock is in use;
 *   - the clock entries were read;
 *   - the role roster was read — without it the clocked-in count cannot
 *     be filtered to front-of-house roles, and an unfiltered count would
 *     be a different (wrong) number rather than a weaker one.
 *
 * Anything else is `'unavailable'`, except a shop that demonstrably does
 * not use the clock, which is `'disabled'`. The distinction between
 * those two is what stops "we could not read the clock" from being
 * treated as "there is no clock".
 */
export function resolveTimeClockStatus(args: {
  shopProfileRead: boolean;
  timeClockEnabled: boolean;
  clockEntriesRead: boolean;
  membershipsRead: boolean;
}): FohTimeClockStatus {
  if (!args.shopProfileRead) return 'unavailable';
  if (!args.timeClockEnabled) return 'disabled';
  if (!args.clockEntriesRead || !args.membershipsRead) return 'unavailable';
  return 'available';
}

// =============================================================================
// Assembly
// =============================================================================

export interface BuildFohObservationsArgs {
  shopId: string;
  windowStartIso: string;
  windowEndIso: string;
  staffing: FohStaffingObservation;
  custodyRows?: ReadonlyArray<FohCustodyRowLite>;
  ticketRows?: ReadonlyArray<FohTicketRowLite>;
  /**
   * Calls plus outbound messages counted over the TRAILING window of
   * equal length. `null` when the sources could not be read.
   */
  trailingCommunicationCount?: number | null;
  /** Advisor decisions counted over the trailing window. `null` when unreadable. */
  trailingDecisionCount?: number | null;
  /** Ids of sources that failed this run, for the basis report. */
  unavailableSources?: ReadonlyArray<string>;
  /** Claim kinds whose source failed and must be reported `'unavailable'`. */
  unavailableClaimKinds?: ReadonlyArray<FohClaimObservation['kind']>;
}

/**
 * Assemble the model's input from already-fetched rows.
 *
 * Communication and decision volume are counted over the PRECEDING window
 * and used as the estimate for this one, because neither can be known in
 * advance — nobody schedules the call they are about to receive. That
 * makes them extrapolations, and they are marked `'trailing_rate'` so the
 * result reports them as such rather than passing an extrapolation off as
 * a count.
 *
 * `technician_support` is emitted as `'unobserved'`. The shop does not
 * capture a technician arriving at the desk with a question, and the
 * honest representation of a demand the shop cannot see is a stated
 * absence, not a zero.
 */
export function buildFohAttentionObservations(
  args: BuildFohObservationsArgs,
): FohAttentionObservations {
  const unavailable = new Set(args.unavailableClaimKinds ?? []);
  const claims: FohClaimObservation[] = [];

  const custodyClaims = aggregateCustodyClaims(
    args.custodyRows ?? [],
    args.windowStartIso,
  );
  for (const claim of custodyClaims) {
    claims.push(
      unavailable.has(claim.kind)
        ? { kind: claim.kind, count: 0, basis: 'unavailable' }
        : claim,
    );
  }

  if (unavailable.has('arrival_intake')) {
    claims.push({ kind: 'arrival_intake', count: 0, basis: 'unavailable' });
  } else {
    const arrivals = countArrivalsInWindow(
      args.ticketRows ?? [],
      args.windowStartIso,
      args.windowEndIso,
    );
    claims.push({
      kind: 'arrival_intake',
      count: arrivals,
      // The shop has no arrival event. Every arrival in this claim is a
      // vehicle the schedule says is coming and that has not yet shown
      // up anywhere physical, so the whole claim is inferred.
      basis: 'inferred',
      presence: { observed: 0, inferred: arrivals },
    });
  }

  if (unavailable.has('waiter_present')) {
    claims.push({ kind: 'waiter_present', count: 0, basis: 'unavailable' });
  } else {
    const presence = countWaitersPresent(
      args.ticketRows ?? [],
      args.windowStartIso,
      args.windowEndIso,
    );
    claims.push({
      kind: 'waiter_present',
      count: presence.observed + presence.inferred,
      basis: presenceBasis(presence),
      presence,
    });
  }

  claims.push(
    typeof args.trailingCommunicationCount === 'number'
      ? {
          kind: 'customer_communication',
          count: args.trailingCommunicationCount,
          basis: 'trailing_rate',
        }
      : { kind: 'customer_communication', count: 0, basis: 'unavailable' },
  );

  claims.push(
    typeof args.trailingDecisionCount === 'number'
      ? {
          kind: 'decision_approval',
          count: args.trailingDecisionCount,
          basis: 'trailing_rate',
        }
      : { kind: 'decision_approval', count: 0, basis: 'unavailable' },
  );

  // Constitutionally enumerated, operationally unobserved. See
  // CORE-BN-GAP-001 follow-through in the capability record.
  claims.push({ kind: 'technician_support', count: 0, basis: 'unobserved' });

  return {
    shopId: args.shopId,
    windowStartIso: args.windowStartIso,
    windowEndIso: args.windowEndIso,
    staffing: args.staffing,
    claims,
    unavailableSources: Object.freeze([...(args.unavailableSources ?? [])]),
  };
}
